import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { LogOut, FileText, Home as HomeIcon, Menu as MenuIcon, LayoutDashboard, Globe, FilePlus, Store, Mail, Heart, BarChart3, Users } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import Logo from "../../components/Logo";
import ArticlesTab from "./ArticlesTab";
import HomeTab from "./HomeTab";
import MenuTab from "./MenuTab";
import PagesTab from "./PagesTab";
import VendorsTab from "./VendorsTab";
import EnquiriesTab from "./EnquiriesTab";
import FeaturedTab from "./FeaturedTab";
import AnalyticsTab from "./AnalyticsTab";
import SubscribersTab from "./SubscribersTab";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "articles", label: "Articles", icon: FileText },
  { id: "home", label: "Home Page", icon: HomeIcon },
  { id: "vendors", label: "Vendors", icon: Store },
  { id: "enquiries", label: "Enquiries", icon: Mail },
  { id: "featured", label: "Get Featured", icon: Heart },
  { id: "subscribers", label: "Subscribers", icon: Users },
  { id: "menu", label: "Menu and Socials", icon: MenuIcon },
  { id: "pages", label: "Pages", icon: FilePlus },
];

function Overview({ go }) {
  const [stats, setStats] = useState({ articles: 0, published: 0, pages: 0, enquiries: 0, featured: 0, subscribers: 0 });
  useEffect(() => {
    Promise.all([api("/admin/blogs"), api("/pages"), api("/admin/enquiries"), api("/admin/featured"), api("/admin/newsletter")])
      .then(([b, p, e, f, s]) => setStats({
        articles: b.length,
        published: b.filter((x) => x.published).length,
        pages: p.length,
        enquiries: e.length,
        featured: f.length,
        subscribers: s.length,
      }))
      .catch(() => {});
  }, []);
  const cards = [
    { label: "Total Articles", value: stats.articles, tab: "articles" },
    { label: "Published", value: stats.published, tab: "articles" },
    { label: "Custom Pages", value: stats.pages, tab: "pages" },
    { label: "Vendor Enquiries", value: stats.enquiries, tab: "enquiries" },
    { label: "Feature Requests", value: stats.featured, tab: "featured" },
    { label: "Subscribers", value: stats.subscribers, tab: "subscribers" },
  ];
  return (
    <div data-testid="admin-overview">
      <h2 className="font-display uppercase text-2xl md:text-4xl text-ink">Welcome back, Editor</h2>
      <p className="mt-2 text-sm text-neutral-500">Everything published here goes live on the site immediately.</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mt-8 md:mt-10">
        {cards.map((c) => (
          <button
            key={c.label}
            data-testid={`overview-card-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => go(c.tab)}
            className="text-left bg-white border-2 border-gold/10 rounded-[1.5rem] p-5 md:p-7 hover:border-gold/50 transition-colors duration-300"
          >
            <p className="font-display text-3xl md:text-5xl text-champagne">{c.value}</p>
            <p className="mt-2 md:mt-3 text-[9px] md:text-[10px] font-bold uppercase tracking-[0.2em] text-ink/60">{c.label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="font-elegant italic text-3xl text-gold animate-pulse">The Brides of India</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const signOut = async () => {
    await logout();
    navigate("/admin/login");
  };

  const tabButton = (t) => (
    <button
      key={t.id}
      data-testid={`admin-tab-${t.id}`}
      onClick={() => setTab(t.id)}
      className={`flex items-center gap-2.5 px-4 py-2.5 rounded-full text-[10px] md:text-[11px] uppercase tracking-[0.15em] font-bold whitespace-nowrap transition-colors duration-300 ${
        tab === t.id ? "bg-champagne text-ink" : "text-cream/60 hover:text-champagne"
      }`}
    >
      <t.icon size={14} />
      {t.label}
    </button>
  );

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-cream flex flex-col md:flex-row">
      <aside className="md:w-64 lg:w-72 md:min-h-screen bg-ink text-cream shrink-0 md:sticky md:top-0 md:h-screen flex flex-col">
        <div className="flex items-center justify-between gap-4 px-4 pt-4 md:p-8 md:pb-0">
          <Logo light />
          <div className="flex md:hidden items-center gap-2">
            <Link to="/" data-testid="admin-view-site-mobile" aria-label="View site"
              className="w-9 h-9 rounded-full border border-cream/20 flex items-center justify-center text-cream/70">
              <Globe size={15} />
            </Link>
            <button onClick={signOut} data-testid="admin-logout-mobile" aria-label="Sign out"
              className="w-9 h-9 rounded-full border border-cream/20 flex items-center justify-center text-cream/70">
              <LogOut size={15} />
            </button>
          </div>
        </div>
        <nav data-testid="admin-tab-bar" className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible px-4 py-4 md:p-8 md:pt-10 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {TABS.map(tabButton)}
        </nav>
        <div className="hidden md:flex md:flex-col gap-2 mt-auto p-8 pt-6 border-t border-cream/10">
          <Link to="/" data-testid="admin-view-site" className="flex items-center gap-3 px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-bold text-cream/60 hover:text-champagne transition-colors duration-300">
            <Globe size={15} /> View Site
          </Link>
          <button onClick={signOut} data-testid="admin-logout-button" className="flex items-center gap-3 px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-bold text-cream/60 hover:text-rose transition-colors duration-300">
            <LogOut size={15} /> Sign Out
          </button>
        </div>
      </aside>

      <section className="flex-1 w-full min-w-0 p-4 sm:p-6 md:p-12">
        {tab === "overview" && <Overview go={setTab} />}
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "articles" && <ArticlesTab />}
        {tab === "home" && <HomeTab />}
        {tab === "vendors" && <VendorsTab />}
        {tab === "enquiries" && <EnquiriesTab />}
        {tab === "featured" && <FeaturedTab />}
        {tab === "subscribers" && <SubscribersTab />}
        {tab === "menu" && <MenuTab />}
        {tab === "pages" && <PagesTab />}
      </section>
    </main>
  );
}
